import HeaderBalancesheet from "components/balancesheet/header/HeaderBalancesheet";
import SectionTitle from "core/section-title/SectionTitle";
import _ from "lodash";
import {
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_ACTION,
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_MESSAGE,
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_MESSAGE_ID,
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_TITLE,
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_TITLE_ID,
  ANNUAL_BALANCESHEET_VALIDATION_MODAL_MESSAGE,
  ANNUAL_BALANCESHEET_VALIDATION_MODAL_MESSAGE_ID,
  ANNUAL_BALANCESHEET_VALIDATION_MODAL_TITLE,
  ANNUAL_BALANCESHEET_VALIDATION_MODAL_TITLE_ID,
  BALANCESHEET_EVALUATION_MESSAGE,
  BALANCESHEET_EVALUATION_STATUS,
  FIELDS_TYPES,
  MISSION_STATUS,
  MODULES_TYPES,
  QUERY_STATUS,
  SAVE_MISSIONS_ACTIONS,
} from "constants/constants";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  displayStructure,
  EvaluationComplete,
  flattenTree,
  formatAnnualBalancesheetPayload,
  formatModuleName,
  getBalancesheetModuleClassNames,
  getFieldFromModules,
  getFieldLineIds,
  getFieldValueById,
  initForm,
  initModuleList,
  MANAGER_PUBLISH_SUCCESS_MODAL_MESSAGES,
  onChangeStructure,
  parseFieldIdAndLine,
  renderButton,
  safeJsonParse,
  shouldOmitBlankDescriptionField,
  transformErrors,
  validateField,
} from "utils/balancesheetHelper";
import PropTypes from "prop-types";
import { useAnnualBalancesheetContext } from "api/balance-sheet/annual/AnnualBalancesheetProvider";
import { useUserContext } from "api/user/UserProvider";
import ValidationModal from "components/modal/ValidationModal";
import SalaryValidationModal from "components/modal/BalancesheetSalaryModal";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ANNUAL_FILE_ACTION,
  ANNUAL_BALANCESHEET_UPLOAD_ACCEPT,
  ANNUAL_BALANCESHEET_UPLOAD_MAX_SIZE,
  ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE,
  getAnnualFileUploadModalMessages,
  getAnnualUploadErrorMessage,
  isAnnualUploadFileValid,
} from "./annualFileUploadHelpers";

const AnnualBalancesheetContent = ({
  data,
  form,
  setForm,
  personById,
  isExport,
}) => {
  const [modules, setModules] = useState([]);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [activeFields, setActiveFields] = useState([]);
  const [rows, setRows] = useState({});
  const [isEvaluationComplete, setIsEvaluationComplete] = useState(false);
  const [showFinishedMessage, setShowFinishedMessage] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationModalMessages, setValidationModalMessages] = useState();
  const [isFinalSubmitting, setIsFinalSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [shouldRedirectToPublishedView, setShouldRedirectToPublishedView] =
    useState(false);
  const [salaryWarning, setSalaryWarning] = useState(null);
  const [isSalaryWarningModalOpen, setIsSalaryWarningModalOpen] =
    useState(false);
  const [isSalaryValidationEnabled, setIsSalaryValidationEnabled] =
    useState(true);
  const [fileUploadStatus, setFileUploadStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState("");
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileActionType, setFileActionType] = useState(
    ANNUAL_FILE_ACTION.UPLOAD
  );
  const [pendingDeletedFileEntryIds, setPendingDeletedFileEntryIds] = useState(
    () => new Set()
  );

  const [localErrors, setLocalErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({});

  const {
    scores,
    setScores,
    missionAverage,
    setMissionAverage,
    updateMissionScores,
    updateMissionAverage,
    updateTotalScore,
    totalScore,
  } = useAnnualBalancesheetContext();
  const { userScope } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { person_id, slug, token } = useParams();

  const dynamicTable = useRef({});
  const hasTriggeredSaveRef = useRef(false);
  const {
    annualBalancesheetByToken,
    saveAnnualEvaluation,
    saveEvaluationStatus,
    savedEvaluation,
    getEvaluationStatus,
    exportAnnualBalanceSheetPdf,
    exportPdfStatus,
    setExportPdfStatus,
    uploadAnnualEvaluationFieldFile,
    downloadAnnualEvaluationFile,
  } = useAnnualBalancesheetContext();
  const isSavingEvaluation = saveEvaluationStatus === QUERY_STATUS.LOADING;
  const isSaveSuccess = saveEvaluationStatus === QUERY_STATUS.SUCCESS;
  const bsStatus = annualBalancesheetByToken?.balancesheet?.status;

  const isConsultantPendingOrCreated = [
    BALANCESHEET_EVALUATION_STATUS.CONSULTANT_PENDING,
    BALANCESHEET_EVALUATION_STATUS.CONSULTANT_CREATED,
  ].includes(bsStatus);

  const isManagerPendingOrCreated = [
    BALANCESHEET_EVALUATION_STATUS.PENDING,
    BALANCESHEET_EVALUATION_STATUS.CREATED,
  ].includes(bsStatus);

  const isPendingOrCreated =
    isConsultantPendingOrCreated || isManagerPendingOrCreated;

  const isFinished = [
    BALANCESHEET_EVALUATION_STATUS.CONSULTANT_FINISHED,
    BALANCESHEET_EVALUATION_STATUS.FINISHED,
    BALANCESHEET_EVALUATION_STATUS.PUBLISHED,
  ].includes(bsStatus);

  const isAlreadySavedOrClosed =
    savedEvaluation?.action === MISSION_STATUS.ALREADY_SAVED &&
    bsStatus !== BALANCESHEET_EVALUATION_STATUS.PUBLISHED;
  const hasPublishAction = savedEvaluation?.action === MISSION_STATUS.PUBLISHED;
  const isPublishedStatus =
    bsStatus === BALANCESHEET_EVALUATION_STATUS.PUBLISHED;
  const isReadOnly = isPublishedStatus;
  const isManagerPublishAction =
    hasPublishAction && isManagerPendingOrCreated && Boolean(person_id);
  const canDownloadAnnualFiles = Boolean(person_id);

  const isSaveError = saveEvaluationStatus === QUERY_STATUS.ERROR;
  const isGetEvaluationError = getEvaluationStatus === QUERY_STATUS.ERROR;
  const fieldKey = (field, lineId = 1) => `${field?.field_id}_${lineId}`;

  const hasApiErrors = !_.isEmpty(savedEvaluation?.errors);
  const isSalaryNotCreated =
    savedEvaluation?.action === ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_ACTION;
  const filteredModules = useMemo(
    () =>
      modules.filter(
        (elm) =>
          elm.type !== MODULES_TYPES.MR_SECOND_MODULE_NT &&
          elm.type !== MODULES_TYPES.MR_SECOND_MODULE
      ),
    [modules]
  );
  const modulesById = useMemo(() => _.keyBy(modules, "module_id"), [modules]);
  const errors = useMemo(
    () => ({ ...serverErrors, ...localErrors }),
    [serverErrors, localErrors]
  );
  const balancesheetFields = useMemo(
    () => modules.flatMap((module) => module?.fields || []),
    [modules]
  );
  const validationModalStatus =
    isValidationModalOpen && (hasApiErrors || isSalaryNotCreated)
      ? QUERY_STATUS.ERROR
      : saveEvaluationStatus;
  const annualUploadExcludedFieldIds = useMemo(() => {
    const fieldIds = new Set();

    filteredModules.forEach((module) => {
      module?.fields?.forEach((field, index) => {
        if (field?.type !== FIELDS_TYPES.FILE) return;

        fieldIds.add(String(field.field_id));

        const hiddenField = module?.fields?.[index - 1];
        if (hiddenField?.type === FIELDS_TYPES.HIDDEN) {
          fieldIds.add(String(hiddenField.field_id));
        }
      });
    });

    return fieldIds;
  }, [filteredModules]);

  const fileUploadModalMessages = useMemo(
    () =>
      getAnnualFileUploadModalMessages(
        fileActionType,
        fileUploadErrorMessage
      ),
    [fileActionType, fileUploadErrorMessage]
  );
  // 1) this useEffect below decides what should be displayed depending on the balancesheet status
  useEffect(() => {
    if (!bsStatus) return;
    if (isPendingOrCreated || isPublishedStatus) {
      setIsEvaluationComplete(false);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
    } else if (isFinished) {
      setIsEvaluationComplete(true);
      setShowFinishedMessage(true);
      setIsLinkExpired(false);
    } else {
      setIsEvaluationComplete(true);
      setShowFinishedMessage(false);
      setIsLinkExpired(true);
    }
  }, [bsStatus, isPendingOrCreated, isFinished, isPublishedStatus]);

  const updateFormFieldValue = ({ targetFieldId, value }) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.field_id === targetFieldId || f.id === targetFieldId
          ? { ...f, value }
          : f
      ),
    }));
  };

  const handleSalaryWarningSubmit = ({ salary, coef }) => {
    const salaryBaseId = salaryWarning?.salary_field_id;
    const coefBaseId = salaryWarning?.coef_field_id;

    const salaryField = findFirstFieldMatch(salaryBaseId);
    const coefField = findFirstFieldMatch(coefBaseId);

    if (salaryField) {
      updateFormFieldValue({
        targetFieldId: salaryField.id,
        value: salary,
      });
    }

    if (coefField) {
      updateFormFieldValue({
        targetFieldId: coefField.id,
        value: coef,
      });
    }
    setIsSalaryValidationEnabled(false);
    setIsSalaryWarningModalOpen(false);
    setSalaryWarning(null);
  };

  const onFieldChange = ({ val, missionId, setForm, form }) => {
    if (isReadOnly) return;

    const value = val?.value;
    const fid = val?.field_id || val?.id;
    const { base, lineId } = parseFieldIdAndLine(fid);

    const field = getFieldFromModules(base, filteredModules) || {
      field_id: base,
      type: FIELDS_TYPES.TEXT,
      required: false,
      options: null,
    };
    const id = fieldKey(field, lineId);
    const payload = {
      ...val,
      id: val?.id ?? id,
      field_id: val?.field_id ?? id,
      value,
      missionId,
    };

    onChangeStructure({
      val: payload,
      setForm,
      form,
      missionId,
    });
    // Validate required and min-words rules for the edited field and return an error or null
    const err = validateField(field, value, id);
    setLocalErrors((prev) => {
      const next = { ...prev };
      if (err) next[id] = err;
      else delete next[id];
      return next;
    });
    setServerErrors((prev) => {
      if (!prev[id]) return prev;

      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const findFirstFieldMatch = (baseFieldId) => {
    const base = String(baseFieldId);

    const matchByFieldId = form?.fields?.find(
      (field) => String(field.field_id) === base
    );
    if (matchByFieldId) return matchByFieldId;

    const matchById = form?.fields?.find((field) => field.id === base);
    if (matchById) return matchById;

    return form?.fields?.find((field) => field.id.startsWith(`${base}_`));
  };

  const getBaseFieldId = (fieldLike) => {
    if (fieldLike?.field_id) {
      return String(parseFieldIdAndLine(fieldLike.field_id).base);
    }

    if (fieldLike?.id) {
      return String(parseFieldIdAndLine(fieldLike.id).base);
    }

    return "";
  };

  const getLineId = (fieldLike) => {
    if (fieldLike?.id) {
      return Number(parseFieldIdAndLine(fieldLike.id).lineId || 1);
    }

    if (fieldLike?.field_id) {
      return Number(parseFieldIdAndLine(fieldLike.field_id).lineId || 1);
    }

    return 1;
  };

  const matchesFieldEntry = (currentField, targetFieldId, lineId = 1) =>
    getBaseFieldId(currentField) === String(targetFieldId) &&
    getLineId(currentField) === Number(lineId);

  const findCompanionHiddenField = ({ field, moduleFields = [] }) => {
    const fileFieldIndex = moduleFields.findIndex(
      (moduleField) =>
        String(moduleField?.field_id) === String(field?.field_id) ||
        String(moduleField?.id) === String(field?.id)
    );

    if (fileFieldIndex <= 0) return null;

    const hiddenField = moduleFields[fileFieldIndex - 1];
    return hiddenField?.type === FIELDS_TYPES.HIDDEN ? hiddenField : null;
  };

  const getAnnualFileEntryIds = ({ field, lineId = 1, moduleFields = [] }) => {
    const hiddenField = findCompanionHiddenField({ field, moduleFields });
    const entryIds = [fieldKey(field, lineId)];

    if (hiddenField?.field_id) {
      entryIds.push(fieldKey(hiddenField, lineId));
    }

    return entryIds;
  };

  const getResponseFieldValue = ({ response, targetFieldId, lineId = 1 }) => {
    const targetBaseFieldId = String(targetFieldId);
    const queue = [response];

    while (queue.length > 0) {
      const current = queue.shift();

      if (Array.isArray(current)) {
        queue.push(...current);
        continue;
      }

      if (!current || typeof current !== "object") continue;

      const currentBaseFieldId = getBaseFieldId({
        id: current?.id,
        field_id: current?.field_id,
      });
      const currentLineId = getLineId({
        id: current?.id,
        field_id: current?.field_id,
      });

      if (
        currentBaseFieldId === targetBaseFieldId &&
        currentLineId === Number(lineId) &&
        Object.prototype.hasOwnProperty.call(current, "value")
      ) {
        return current.value;
      }

      Object.values(current).forEach((value) => {
        if (value && typeof value === "object") {
          queue.push(value);
        }
      });
    }

    return undefined;
  };

  const upsertLineFieldState = ({
    sourceFields = [],
    field,
    value,
    lineId = 1,
  }) => {
    if (!field?.field_id) {
      return sourceFields;
    }

    const targetId = fieldKey(field, lineId);
    let hasUpdated = false;

    const nextFields = sourceFields.map((currentField) => {
      if (
        matchesFieldEntry(currentField, field?.field_id, lineId) ||
        currentField?.id === targetId ||
        currentField?.field_id === targetId
      ) {
        hasUpdated = true;
        return {
          ...currentField,
          id: currentField?.id || targetId,
          field_id: currentField?.field_id || field?.field_id,
          value,
        };
      }

      return currentField;
    });

    if (hasUpdated) {
      return nextFields;
    }

    return [
      ...nextFields,
      {
        id: targetId,
        field_id: field?.field_id,
        mission_id: field?.mission_id,
        value,
      },
    ];
  };

  const updateUploadedFieldState = ({
    field,
    fileName,
    lineId = 1,
    moduleFields = [],
    response,
  }) => {
    const hiddenField = findCompanionHiddenField({ field, moduleFields });
    const resolvedFileName =
      getResponseFieldValue({
        response,
        targetFieldId: field?.field_id,
        lineId,
      }) ||
      response?.file_name ||
      fileName;
    const resolvedStoredFileId = hiddenField
      ? getResponseFieldValue({
          response,
          targetFieldId: hiddenField?.field_id,
          lineId,
        }) ?? response?.file_id
      : undefined;

    setForm((prev) => ({
      ...prev,
      fields: (() => {
        let nextFields = upsertLineFieldState({
          sourceFields: prev?.fields || [],
          field,
          value: resolvedFileName,
          lineId,
        });

        if (hiddenField && !_.isNil(resolvedStoredFileId)) {
          nextFields = upsertLineFieldState({
            sourceFields: nextFields,
            field: hiddenField,
            value: resolvedStoredFileId,
            lineId,
          });
        }

        return nextFields;
      })(),
    }));
    const uploadedEntryIds = getAnnualFileEntryIds({
      field,
      lineId,
      moduleFields,
    });

    setPendingDeletedFileEntryIds((prev) => {
      const next = new Set(prev);
      uploadedEntryIds.forEach((entryId) => next.delete(entryId));
      return next;
    });
  };

  const buildClearedUploadedFieldState = ({
    sourceFields = [],
    field,
    lineId = 1,
    moduleFields = [],
  }) => {
    const hiddenField = findCompanionHiddenField({ field, moduleFields });

    let nextFields = upsertLineFieldState({
      sourceFields,
      field,
      value: "",
      lineId,
    });

    if (hiddenField) {
      nextFields = upsertLineFieldState({
        sourceFields: nextFields,
        field: hiddenField,
        value: "",
        lineId,
      });
    }

    return nextFields;
  };

  const buildPendingUploadedFieldState = ({
    sourceFields = [],
    field,
    fileName,
    lineId = 1,
  }) =>
    upsertLineFieldState({
      sourceFields,
      field,
      value: fileName,
      lineId,
    });

  const getAnnualUploadedStoredFileId = ({
    field,
    lineId = 1,
    moduleFields = [],
  }) => {
    const hiddenField = findCompanionHiddenField({ field, moduleFields });

    if (!hiddenField) {
      return "";
    }

    return (
      getFieldValueById(form?.fields, hiddenField?.field_id, lineId) ||
      getFieldValueById(form?.fields, hiddenField?.id, lineId) ||
      ""
    );
  };

  const handleAnnualFileDownload = async ({
    field,
    lineId = 1,
    moduleFields = [],
  }) => {
    const storedFileId = getAnnualUploadedStoredFileId({
      field,
      lineId,
      moduleFields,
    });
    const fileName = getFieldValueById(form?.fields, field?.field_id, lineId);

    if (!storedFileId || !fileName) {
      return;
    }

    try {
      await downloadAnnualEvaluationFile({
        file_id: storedFileId,
        file_name: fileName,
      });
    } catch (error) {
      console.error("handleAnnualFileDownload", error);
    }
  };

  const handleAnnualFileUpload = async ({
    field,
    file,
    lineId = 1,
    moduleFields = [],
    moduleId,
  }) => {
    if (isReadOnly || !file) return;

    if (file.size > ANNUAL_BALANCESHEET_UPLOAD_MAX_SIZE) {
      setFileUploadErrorMessage(
        ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE.FILE_TOO_LARGE
      );
      setFileActionType(ANNUAL_FILE_ACTION.UPLOAD);
      setFileUploadStatus(QUERY_STATUS.ERROR);
      return;
    }

    if (!isAnnualUploadFileValid(file)) {
      setFileUploadErrorMessage(
        ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE.INVALID_EXTENSION
      );
      setFileActionType(ANNUAL_FILE_ACTION.UPLOAD);
      setFileUploadStatus(QUERY_STATUS.ERROR);
      return;
    }

    setFileActionType(ANNUAL_FILE_ACTION.UPLOAD);
    setFileUploadErrorMessage("");
    setFileUploadStatus(QUERY_STATUS.LOADING);
    setIsUploadingFile(true);

    const previousFields = (form?.fields || []).map((formField) => ({
      ...formField,
    }));
    const pendingFields = buildPendingUploadedFieldState({
      sourceFields: previousFields,
      field,
      fileName: file.name,
      lineId,
    });

    setForm((prev) => ({
      ...prev,
      fields: pendingFields,
    }));

    try {
      const response = await uploadAnnualEvaluationFieldFile({
        evaluation_id: annualBalancesheetByToken?.balancesheet?.id,
        field_id: field?.field_id,
        file,
        module_id: moduleId,
        slug,
        token,
      });
      updateUploadedFieldState({
        field,
        fileName: file.name,
        lineId,
        moduleFields,
        response,
      });
      setFileUploadStatus(QUERY_STATUS.SUCCESS);
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        fields: previousFields,
      }));
      setFileUploadErrorMessage(
        getAnnualUploadErrorMessage(error, ANNUAL_FILE_ACTION.UPLOAD)
      );
      setFileUploadStatus(QUERY_STATUS.ERROR);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleAnnualFileDelete = async ({
    field,
    lineId = 1,
    moduleFields = [],
  }) => {
    if (isReadOnly) return;

    const sourceFields = (form?.fields || []).map((formField) => ({
      ...formField,
    }));
    const clearedFields = buildClearedUploadedFieldState({
      sourceFields,
      field,
      lineId,
      moduleFields,
    });
    const fileEntryIds = getAnnualFileEntryIds({
      field,
      lineId,
      moduleFields,
    });

    setFileActionType(ANNUAL_FILE_ACTION.DELETE);
    setFileUploadErrorMessage("");
    setPendingDeletedFileEntryIds((prev) => {
      const next = new Set(prev);
      fileEntryIds.forEach((entryId) => next.add(entryId));
      return next;
    });
    setForm((prev) => ({
      ...prev,
      fields: clearedFields,
    }));
    setFileUploadStatus(QUERY_STATUS.SUCCESS);
  };

  const coefSelectOptions = useMemo(() => {
    if (!isSalaryWarningModalOpen || !salaryWarning?.coef_field_id) return [];

    const coefFieldId = Number(
      String(salaryWarning.coef_field_id).split("_")[0]
    );

    const coefField = filteredModules
      .flatMap((m) => m.fields || [])
      .find((f) => Number(f.field_id) === coefFieldId);

    return JSON.parse(coefField.options).values.map((coef) => ({
      value: coef.val,
      label: coef.lb,
    }));
  }, [isSalaryWarningModalOpen, salaryWarning, filteredModules]);

  const checkPageErrors = () => {
    if (isPublishedStatus) return true;

    const pageModules = filteredModules.filter(
      (module) => module?.page === pageIndex + 1
    );
    const nextErrors = {};

    pageModules.forEach((pageModule) => {
      const moduleRows = rows?.[pageModule?.module_id];

      (pageModule?.fields || []).forEach((field) => {
        getFieldLineIds(field, moduleRows).forEach((lineId) => {
          const id = fieldKey(field, lineId);
          const val = getFieldValueById(form?.fields, field?.field_id, lineId);
          const err = validateField(field, val, id);
          if (err) nextErrors[id] = err;
        });
      });
    });

    setLocalErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const navigateToNextPageIfValid = () => {
    setIsValidated(true);
    if (!isPublishedStatus && !checkPageErrors()) {
      window.scrollTo(0, 0);

      return;
    }
    setIsValidated(false);
    setPageIndex((currentPageIndex) => currentPageIndex + 1);
    window.setTimeout(() => {
      window.scrollTo?.(0, 0);
    }, 0);
  };
  useEffect(() => {
    // 1- in case we have a loading error on the current evaluation
    if (isGetEvaluationError) {
      setIsFinalSubmitting(false);
      setHasError(true);
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(true);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      return;
    }

    // Ignore stale save results kept in context from a previous visit.
    if (!hasTriggeredSaveRef.current) return;

    // 2- in case the current save request ended in error
    if (isSaveError) {
      setIsFinalSubmitting(false);
      setHasError(true);
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(true);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      return;
    }

    // nothing to do as long as the request is not finished with SUCCESS
    if (!isSaveSuccess) return;

    setHasError(false);

    // 2) if api errors : stay on the form
    if (hasApiErrors) {
      setServerErrors(transformErrors(savedEvaluation?.errors));
      setValidationModalMessages({
        errorTitleId: ANNUAL_BALANCESHEET_VALIDATION_MODAL_TITLE_ID,
        errorTitleDefault: ANNUAL_BALANCESHEET_VALIDATION_MODAL_TITLE,
        errorMessageId: ANNUAL_BALANCESHEET_VALIDATION_MODAL_MESSAGE_ID,
        errorMessageDefault: ANNUAL_BALANCESHEET_VALIDATION_MODAL_MESSAGE,
      });
      setShouldRedirectToPublishedView(false);
      setIsValidationModalOpen(true);
      setIsValidated(true);
      setIsFinalSubmitting(false);
      setIsEvaluationComplete(false);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      return;
    }

    setServerErrors({});

    // 3) salary line not created on manager publish : stay on the form, the evaluation is not validated
    if (isSalaryNotCreated) {
      setValidationModalMessages({
        errorTitleId: ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_TITLE_ID,
        errorTitleDefault: ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_TITLE,
        errorMessageId: ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_MESSAGE_ID,
        errorMessageDefault:
          ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_MODAL_MESSAGE,
      });
      setShouldRedirectToPublishedView(false);
      setIsValidationModalOpen(true);
      setIsFinalSubmitting(false);
      setIsEvaluationComplete(false);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      return;
    }

    if (savedEvaluation?.warning) {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsValidationModalOpen(false);
      setIsFinalSubmitting(false);
      setIsEvaluationComplete(false);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      setSalaryWarning(savedEvaluation?.warning);
      setIsSalaryWarningModalOpen(true);
      return;
    }
    setPendingDeletedFileEntryIds(new Set());
    setIsFinalSubmitting(false);

    if (isDraftSaving === SAVE_MISSIONS_ACTIONS.SAVE) {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(false);
      setIsLinkExpired(false);
      setShowFinishedMessage(false);
      return;
    }
    // Manager validation: confirm success, then reopen the form in read-only published mode.
    if (isManagerPublishAction) {
      setValidationModalMessages(MANAGER_PUBLISH_SUCCESS_MODAL_MESSAGES);
      setShouldRedirectToPublishedView(true);
      setIsValidationModalOpen(true);
      setIsEvaluationComplete(false);
      setShowFinishedMessage(false);
      setIsLinkExpired(false);
      return;
    }
    // 2) if the action is "published", show the EvaluationComplete screen with the SAVED message
    if (hasPublishAction) {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(true);
      setShowFinishedMessage(false);
      setIsLinkExpired(false);
    }
    // 3) if the action is "evaluation-already-saved-or-closed", show the EvaluationComplete screen with the already saved message
    else if (isAlreadySavedOrClosed) {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(true);
      setShowFinishedMessage(true);
      setIsLinkExpired(false);
    }
    // 4) autre action , show the EvaluationComplete screen with expired message
    else {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsEvaluationComplete(true);
      setShowFinishedMessage(false);
      setIsLinkExpired(true);
    }

    const evaluationKey = `isEvaluationComplete_${personById?.id}_${annualBalancesheetByToken?.balancesheet?.id}`;
    localStorage.setItem(evaluationKey, "true");
  }, [
    isSaveSuccess,
    isSaveError,
    isGetEvaluationError,
    hasApiErrors,
    isSalaryNotCreated,
    hasPublishAction,
    isAlreadySavedOrClosed,
    isManagerPublishAction,
    annualBalancesheetByToken?.balancesheet?.id,
    isDraftSaving,
  ]);

  const validateLastPage = (e) => {
    e.preventDefault();
    setIsValidated(true);
    checkPageErrors();
    setIsFinalSubmitting(true);
    submitFormChanges(SAVE_MISSIONS_ACTIONS.PUBLISH);
  };

  useEffect(() => {
    if (data) {
      const structure = JSON.parse(data?.structure)?.pages;
      setPages(Object.keys(structure || {}).map((elm) => structure[elm]));
      setModules(_.drop(flattenTree(data), 1));
      setLocalErrors({});
      setServerErrors({});
      setPendingDeletedFileEntryIds(new Set());

      const moduleListfields = _.chain(_.drop(flattenTree(data), 1))
        .filter(
          (elm) =>
            elm?.type === MODULES_TYPES.MR_IC_MODULE ||
            elm?.type === MODULES_TYPES.MR_MISSION_MODULE
        )
        .map("fields")
        .flattenDeep()
        .filter((elm) => elm?.type === FIELDS_TYPES.INLINE_RADIO)
        .value();
      setActiveFields(
        moduleListfields.map((elm) => `_${elm?.position}_${elm?.value || "1"}`)
      );

      setForm((old) => ({
        ...old,
        fields: _.flatten(
          _.drop(flattenTree(data), 1).map((elm) =>
            initForm({
              fields: elm.fields,
              missionId: elm.mission_id,
            })
          )
        ).concat(
          initModuleList({
            fields: moduleListfields,
          })
        ),
      }));
    }
  }, [data]);

  const handleExportPdf = () => {
    const person_id = annualBalancesheetByToken?.person_id;
    const evaluation_id = annualBalancesheetByToken?.balancesheet?.id;

    const url = new URL(window.location.href);
    url.searchParams.set("export", "true");
    if (person_id && evaluation_id) {
      exportAnnualBalanceSheetPdf({
        scope: userScope,
        person_id,
        evaluation_id,
      });
    }
  };

  const renderExportButton = () => {
    // the button should be hidden if we are in print/export mode
    //and should be displayed if the status of the eval is only PUBLISHED
    if (isExport || !isPublishedStatus) return null;

    return renderButton({
      className: "btn-standard btn-outline",
      onClick: handleExportPdf,
      id: "button-export-pdf",
      messageId: "export_pdf",
      defaultMessage: "Exporter en PDF",
      disabled: exportPdfStatus === QUERY_STATUS.LOADING,
    });
  };

  const updateRowsForModule = (moduleId, newRows) => {
    setRows((prevRows) => ({
      ...prevRows,
      [moduleId]: newRows,
    }));
  };

  const onChangeTableValues = (vals) => {
    if (isReadOnly) return;

    setForm((prevForm) => {
      const nonTableFields = prevForm.fields.filter(
        (field) => !vals.some((val) => field.id === val.id)
      );

      const updatedTableFields = vals.map((val) => ({
        id: val.id,
        value: val.value,
        field_id: val.field_id,
      }));

      return {
        ...prevForm,
        fields: [...nonTableFields, ...updatedTableFields],
      };
    });
  };

  const getTableModuleId = (fieldId, tableModules) => {
    const module = tableModules.find((module) =>
      module.fields?.some((field) => field.field_id === fieldId)
    );
    return module?.module_id;
  };

  const rowContainsUserData = (row) =>
    Object.entries(row || {}).some(
      ([key, val]) =>
        key !== "id" &&
        val !== undefined &&
        val !== null &&
        String(val).trim() !== ""
    );

  const shouldIncludeTableField = (elm, tableModules, tableRows) => {
    const moduleId = getTableModuleId(elm.field_id, tableModules);
    if (!moduleId) return true;

    const rows = tableRows?.[moduleId];
    if (!rows || rows.length === 0) return false;
    const { lineId } = parseFieldIdAndLine(elm?.id);
    const currentRow = rows.find((row) => row?.id === lineId);

    if (!currentRow) return false;

    return rowContainsUserData(currentRow);
  };

  const findInitialFieldDefinition = (formField) => {
    const baseFieldId = getBaseFieldId(formField);

    return balancesheetFields.find(
      (field) =>
        String(field?.field_id) === baseFieldId ||
        String(field?.id) === baseFieldId ||
        field?.id === formField?.id
    );
  };

  const buildAnnualSavePayloadFields = ({
    sourceFields = [],
    includeFileEntryIds = new Set(),
  }) => {
    const tableModules = filteredModules.filter(
      (m) => m.type === MODULES_TYPES.TABLE
    );

    return _.uniqBy(
      sourceFields.filter((currentField) => currentField?.id),
      "id"
    )
      .filter((currentField) => {
        const baseFieldId = getBaseFieldId(currentField);
        const hasValue =
          !_.isNil(currentField?.value) && currentField?.value !== "";

        return (
          !annualUploadExcludedFieldIds.has(baseFieldId) ||
          includeFileEntryIds.has(currentField?.id) ||
          hasValue
        );
      })
      .filter((currentField) =>
        shouldIncludeTableField(currentField, tableModules, rows)
      )
      .filter((currentField) => {
        const initialField = findInitialFieldDefinition(currentField);

        return !shouldOmitBlankDescriptionField({
          currentValue: currentField?.value,
          initialField,
        });
      })
      .map((currentField) => ({
        id: currentField.id,
        fieldId: currentField.field_id,
        value: currentField.value,
        missionId: currentField.mission_id,
      }));
  };

  const submitFormChanges = (actionType) => {
    hasTriggeredSaveRef.current = true;
    setServerErrors({});
    const totalForm = buildAnnualSavePayloadFields({
      sourceFields: form?.fields || [],
      includeFileEntryIds: pendingDeletedFileEntryIds,
    });

    const payload = formatAnnualBalancesheetPayload(totalForm, actionType, {
      balancesheetFields,
      processedBy: annualBalancesheetByToken?.person_id,
      salary_validation: isSalaryValidationEnabled,
    });

    setIsDraftSaving(actionType);

    if (
      actionType === SAVE_MISSIONS_ACTIONS.SAVE ||
      actionType === SAVE_MISSIONS_ACTIONS.PUBLISH
    ) {
      setValidationModalMessages(undefined);
      setShouldRedirectToPublishedView(false);
      setIsValidationModalOpen(true);
    }

    saveAnnualEvaluation({
      id: annualBalancesheetByToken?.balancesheet?.id,
      data: payload,
    });
  };

  const getActiveField = (val) => {
    const startVal = _.chain(val).split("_", 2).join("_").value();
    const index = _.findIndex(activeFields, (elm) =>
      _.startsWith(elm, startVal)
    );
    if (index === -1) {
      setActiveFields((old) => [...old, val]);
    } else {
      setActiveFields((old) =>
        old.map((elm) => (_.startsWith(elm, startVal) ? val : elm))
      );
    }
  };

  const renderSaveDraftButton = () =>
    pageIndex === pages.length - 1 &&
    renderButton({
      className: "btn-standard btn-outline",
      onClick: () => {
        setIsValidationModalOpen(true);
        submitFormChanges(SAVE_MISSIONS_ACTIONS.SAVE);
      },
      id: "button-save-draft",
      messageId: "save_draft",
      defaultMessage: "Sauvegarder comme brouillon",
    });

  const renderPreviousPageButton = () =>
    pageIndex > 0 &&
    pageIndex != pages.length - 1 &&
    renderButton({
      className: "btn-standard btn-outline",
      onClick: () => setPageIndex(pageIndex - 1),
      id: "button-previous-page",
      messageId: "previous_page",
      defaultMessage: "Retourner à l'étape précédente",
    });

  const renderNextPageButton = () =>
    pageIndex < pages.length - 1 &&
    renderButton({
      color: "btn-medium gray-700",
      onClick: navigateToNextPageIfValid,
      id: "button-next-page",
      messageId: "next_page",
      defaultMessage: isPublishedStatus
        ? "Aller à l'étape suivante"
        : "Valider et aller à l'étape suivante",
    });

  const renderValidationButton = () =>
    pageIndex === pages.length - 1 &&
    renderButton({
      color: "btn-medium gray-700",
      onClick: validateLastPage,
      id: "button-form-validation-final",
      messageId: "button_balancesheet_validation_all",
      defaultMessage: "Valider toutes les étapes",
      disabled: isFinalSubmitting || isSavingEvaluation,
    });

  // Block forward navigation if the current page is invalid
  const handleStepperChange = (nextIndex) => {
    if (nextIndex === pageIndex) return;

    // Always allow navigating backward (no validation)
    if (nextIndex < pageIndex) {
      setIsValidated(false);
      setPageIndex(nextIndex);
      return;
    }
    setIsValidated(true);
    // Attempt to move forward → validate current page first
    if (!checkPageErrors()) {
      window.scrollTo?.(0, 0);
      return; // stop if invalid
    }
    setIsValidated(false);
    // Valid → proceed to requested step
    setPageIndex(nextIndex);
  };

  const shouldShowEvaluationComplete = isEvaluationComplete;

  const handleValidationModalClose = () => {
    hasTriggeredSaveRef.current = false;
    setIsValidationModalOpen(false);
    setValidationModalMessages(undefined);

    if (!shouldRedirectToPublishedView) return;

    setShouldRedirectToPublishedView(false);

    const evaluationId = annualBalancesheetByToken?.balancesheet?.id;
    if (!person_id || !evaluationId) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("evaluation_id", evaluationId);
    searchParams.set("published", "1");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  return (
    <>
      <DocumentDownloadModal
        status={exportPdfStatus}
        onClose={() => setExportPdfStatus(QUERY_STATUS.INITIAL)}
      />

      <ValidationModal
        open={isValidationModalOpen}
        status={validationModalStatus}
        messages={validationModalMessages}
        onClose={handleValidationModalClose}
      />

      <ValidationModal
        open={fileUploadStatus !== QUERY_STATUS.INITIAL}
        status={fileUploadStatus}
        onClose={() => {
          setFileUploadStatus(QUERY_STATUS.INITIAL);
          setFileUploadErrorMessage("");
          setFileActionType(ANNUAL_FILE_ACTION.UPLOAD);
        }}
        messages={fileUploadModalMessages}
      />

      <SalaryValidationModal
        open={isSalaryWarningModalOpen}
        warning={salaryWarning}
        coefOptions={coefSelectOptions}
        onClose={() => {
          setIsSalaryWarningModalOpen(false);
          setSalaryWarning(null);
        }}
        onCancel={() => {
          setIsSalaryValidationEnabled(false);
        }}
        onSubmit={handleSalaryWarningSubmit}
      />
      {shouldShowEvaluationComplete ? (
        <EvaluationComplete
          icon="dot-note"
          consultantName={[personById?.first_name, personById?.last_name]
            .filter(Boolean)
            .join(" ")}
          title="ÉVALUATION PROFESSIONNELLE"
          message={
            hasError
              ? BALANCESHEET_EVALUATION_MESSAGE.ERROR
              : showFinishedMessage
                ? BALANCESHEET_EVALUATION_MESSAGE.FINISHED
                : isLinkExpired
                  ? BALANCESHEET_EVALUATION_MESSAGE.EXPIRED
                  : BALANCESHEET_EVALUATION_MESSAGE.SAVED
          }
        />
      ) : (
        <>
          <div
            className={`annual-bilan-export-wrapper${
              isExport ? " annual-bilan-export-mode" : ""
            }`}
          >
            <div className="header-wrapper-relative">
              <HeaderBalancesheet
                user={personById}
                pages={pages}
                getPageIndex={handleStepperChange}
                pageIndex={pageIndex}
                setPageIndex={handleStepperChange}
                isExport={isExport}
              />

              <div className="header-export-action">{renderExportButton()}</div>
            </div>

            {_.chain(filteredModules)
              //if isExport is true, return true for ALL modules (no pagination)
              .filter((elm) => (isExport ? true : elm?.page === pageIndex + 1))
              .map((elm) => {
                const {
                  module_id: moduleId,
                  mission_id: missionId,
                  structure,
                  module_title: moduleTitle,
                  fields,
                  type,
                } = elm || {};
                const parsedStructure = safeJsonParse(structure);
                const moduleClass = parsedStructure.moduleClass
                  ? "balancesheet-module-class"
                  : "";
                const parentModule = modulesById[elm?.parent_module_id];
                const moduleClassName = getBalancesheetModuleClassNames({
                  module: elm,
                });

                const rowsForTables = rows[moduleId] || [];
                return (
                  <div
                    className={moduleClassName}
                    data-module-type={type}
                    data-parent-module-type={parentModule?.type}
                    // module_id is shared by every mission instance of a
                    // mission module: mission_id makes the key unique
                    key={`${moduleId}_${missionId}`}
                  >
                    {moduleTitle && (
                      <>
                        <SectionTitle
                          title={moduleTitle}
                          icon={structure}
                          isExport={isExport}
                        />
                        <br />
                      </>
                    )}

                    <div className={`${moduleClass} display-fields`}>
                      {structure &&
                        displayStructure({
                          moduleId,
                          missionId,
                          activeFields,
                          dynamicTable,
                          fields,
                          getActiveField,
                          moduleName: formatModuleName(moduleTitle),
                          onChangeTableValues,
                          structure,
                          type,
                          values: form?.fields,
                          modules,
                          scores,
                          errors,
                          isValidated,
                          setScores,
                          missionAverage,
                          rows: rowsForTables,
                          setRows: (newRows) =>
                            updateRowsForModule(moduleId, newRows),
                          setMissionAverage,
                          updateMissionAverage,
                          updateMissionScores,
                          updateTotalScore,
                          totalScore,
                          readOnly: isReadOnly,
                          onChange: (val) =>
                            onFieldChange({
                              val,
                              setForm,
                              form,
                              missionId,
                            }),
                          fileUploadConfig: {
                            accept: ANNUAL_BALANCESHEET_UPLOAD_ACCEPT,
                            allowDelete: true,
                            icon: "folder",
                            uploadIcon: "add",
                            isBottomIconClickable: false,
                            isUploading: isUploadingFile,
                            onFileUpload: handleAnnualFileUpload,
                            onFileDelete: handleAnnualFileDelete,
                            onFilePreview: canDownloadAnnualFiles
                              ? handleAnnualFileDownload
                              : undefined,
                          },
                          isExport,
                        })}
                    </div>
                  </div>
                );
              })
              .value()}
            {isExport ? null : (
              <div className="balancesheet-page-button">
                {!isPublishedStatus && renderSaveDraftButton()}
                {renderPreviousPageButton()}
                {renderNextPageButton()}
                {!isPublishedStatus && renderValidationButton()}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AnnualBalancesheetContent;

AnnualBalancesheetContent.propTypes = {
  data: PropTypes.object,
  form: PropTypes.object,
  setForm: PropTypes.func,
  personById: PropTypes.object,
};
